/* ═══════════════════════════════════════════════════════════
   KALLISTIA — Skin Quiz JS
   State machine, API calls, dynamic question rendering,
   screen management, email capture, results.
   Supports two surfaces: drawer (sitewide) and embed (homepage).
   ═══════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  var API_BASE = 'https://app.kallistia.com/api/storefront/quiz';

  /* ── SVG constants ── */
  var SVG_ARROW_RIGHT = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#454545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
  var SVG_ARROW_LEFT = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#454545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>';
  var SVG_CHECK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var SVG_STAR = '<svg width="16" height="16" viewBox="0 0 24 24" fill="#f7aea6" stroke="#f7aea6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>';
  var SVG_CHECK_PINK = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f7aea6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var SVG_CHECK_DARK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#454545" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var SVG_SHIELD = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#454545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>';
  var SVG_MATCH_CHECK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#454545" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  /* ═══════════════════════════════════════════════════════════
     QUIZ INSTANCE
     Each surface (drawer, embed) gets its own instance.
     ═══════════════════════════════════════════════════════════ */

  function QuizInstance(rootEl) {
    this.root = rootEl;
    this.mode = rootEl.getAttribute('data-kl-quiz-mode');
    this.slug = rootEl.getAttribute('data-kl-quiz-slug');
    this.config = null;
    this.answers = {};
    this.answerIds = {};
    this.sessionId = null;
    this.currentIndex = 0;
    this.screens = [];
    this.email = '';
    this.emailCaptured = false;
    this.recommendedHandles = [];
    this.productCache = {};
    this.configLoaded = false;
    this.configError = false;
    this.questionCount = 0;
    this.submitPromise = null;

    this._bindStaticEvents();
    this._restoreEmail();
  }

  /* ── Query helper (scoped to instance root) ── */
  QuizInstance.prototype.$ = function(sel) {
    return this.root.querySelector(sel);
  };
  QuizInstance.prototype.$$ = function(sel) {
    return this.root.querySelectorAll(sel);
  };

  /* ── Restore email from sessionStorage ── */
  QuizInstance.prototype._restoreEmail = function() {
    try {
      var saved = sessionStorage.getItem('kl-quiz-email');
      if (saved) {
        this.email = saved;
        this.emailCaptured = true;
      }
    } catch (e) { /* sessionStorage blocked */ }
  };

  /* ── Bind events on static markup (email, analyzing retry, etc.) ── */
  QuizInstance.prototype._bindStaticEvents = function() {
    var self = this;

    // Email submit
    var submitBtn = this.$('[data-kl-quiz-email-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function() { self._submitEmail(); });
    }

    // Email input events
    var emailInput = this.$('[data-kl-quiz-email-input]');
    if (emailInput) {
      emailInput.addEventListener('input', function() {
        this.classList.remove('error');
        var errEl = self.$('[data-kl-quiz-email-error]');
        if (errEl) errEl.classList.remove('show');
      });
      emailInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          self._submitEmail();
        }
      });
    }

    // Retry config
    var retryConfig = this.$('[data-kl-quiz-retry-config]');
    if (retryConfig) {
      retryConfig.addEventListener('click', function() { self.loadConfig(); });
    }

    // Retry submit
    var retrySubmit = this.$('[data-kl-quiz-retry-submit]');
    if (retrySubmit) {
      retrySubmit.addEventListener('click', function() { self._runAnalyzing(); });
    }
  };

  /* ═══════════════════════════════════════════════════════════
     CONFIG LOADING
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype.loadConfig = function(callback) {
    var self = this;

    // Check sessionStorage cache first
    try {
      var cached = sessionStorage.getItem('kl-quiz-cfg2-' + this.slug);
      if (cached) {
        var parsed = JSON.parse(cached);
        self.config = parsed;
        self.configLoaded = true;
        self._buildQuestionScreens();
        if (callback) callback();
        return;
      }
    } catch (e) { /* no cache or parse error */ }

    // Fetch from API
    fetch(API_BASE + '/' + encodeURIComponent(this.slug))
      .then(function(res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function(data) {
        self.config = data;
        self.configLoaded = true;
        self.configError = false;

        // Cache in sessionStorage
        try {
          sessionStorage.setItem('kl-quiz-cfg2-' + self.slug, JSON.stringify(data));
        } catch (e) { /* quota exceeded */ }

        self._buildQuestionScreens();
        if (callback) callback();
      })
      .catch(function() {
        self.configError = true;
        self._showConfigError();
      });
  };

  QuizInstance.prototype._showConfigError = function() {
    var errEl = this.$('[data-kl-quiz-cfg2-error]');
    if (errEl) errEl.style.display = '';
    var screens = this.$('[data-kl-quiz-screens]');
    if (screens) screens.style.display = 'none';
  };

  /* ═══════════════════════════════════════════════════════════
     BUILD QUESTION SCREENS (from API data)
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._buildQuestionScreens = function() {
    if (!this.config || !this.config.questions) return;

    var questions = this.config.questions;
    this.questionCount = questions.length;
    var container = this.$('[data-kl-quiz-screens]');
    if (!container) return;

    // Remove any previously built question screens (prevents duplicates on re-init)
    var existing = container.querySelectorAll('[data-kl-quiz-screen^="q-"]');
    for (var x = 0; x < existing.length; x++) {
      existing[x].parentNode.removeChild(existing[x]);
    }

    // Insert question screens before the email screen
    var emailScreen = this.$('[data-kl-quiz-screen="email"]');

    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      var screenEl = this._buildQuestionScreen(q, i);
      container.insertBefore(screenEl, emailScreen);
    }

    // Hide skeleton
    var skeleton = this.root.querySelector('[data-kl-quiz-skeleton]');
    if (skeleton) skeleton.classList.add('hidden');

    // Build screens array
    this._buildScreensList();
  };

  QuizInstance.prototype._buildQuestionScreen = function(question, index) {
    var self = this;
    var qId = question.id || question.slug || ('q' + (index + 1));
    var isMulti = question.type === 'multi';
    var isTone = question.display_type === 'tone-grid';

    var screen = document.createElement('div');
    screen.className = 'kl-quiz-screen';
    screen.setAttribute('data-kl-quiz-screen', 'q-' + (index + 1));

    var inner = document.createElement('div');
    inner.className = 'kl-quiz-question';

    // Question number
    var qnum = document.createElement('div');
    qnum.className = 'kl-quiz-qnum';
    qnum.textContent = 'Question ' + (index + 1) + ' of ' + this.config.questions.length;
    inner.appendChild(qnum);

    // Question title
    var h2 = document.createElement('h2');
    h2.textContent = question.title || question.text;
    inner.appendChild(h2);

    // Multi-select hint
    if (isMulti) {
      var hint = document.createElement('p');
      hint.className = 'kl-quiz-hint';
      hint.textContent = 'Select all that apply';
      inner.appendChild(hint);
    }

    // Options
    var optionsContainer;
    if (isTone) {
      optionsContainer = document.createElement('div');
      optionsContainer.className = 'kl-quiz-tone-grid';
      optionsContainer.setAttribute('data-kl-quiz-question', qId);
    } else {
      optionsContainer = document.createElement('div');
      optionsContainer.className = 'kl-quiz-options';
      optionsContainer.setAttribute('data-kl-quiz-question', qId);
      if (isMulti) optionsContainer.setAttribute('data-kl-quiz-multi', 'true');
    }

    var answers = question.answers || question.options || [];
    for (var j = 0; j < answers.length; j++) {
      var answer = answers[j];
      if (isTone) {
        optionsContainer.appendChild(this._buildToneButton(answer, qId));
      } else {
        optionsContainer.appendChild(this._buildOptionButton(answer, qId, isMulti));
      }
    }

    inner.appendChild(optionsContainer);

    // Nav (back + continue)
    var nav = document.createElement('div');
    nav.className = 'kl-quiz-nav';

    var backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'kl-quiz-back';
    backBtn.innerHTML = SVG_ARROW_LEFT + ' Back';
    backBtn.addEventListener('click', function() { self._goBack(); });
    nav.appendChild(backBtn);

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'kl-quiz-next';
    nextBtn.setAttribute('data-kl-quiz-nav-next', qId);
    nextBtn.innerHTML = 'Continue ' + SVG_ARROW_RIGHT;
    nextBtn.addEventListener('click', function() { self._goNext(); });
    nav.appendChild(nextBtn);

    inner.appendChild(nav);
    screen.appendChild(inner);
    return screen;
  };

  QuizInstance.prototype._buildOptionButton = function(answer, qId, isMulti) {
    var self = this;
    var value = answer.text || '';
    var label = answer.text || '';
    var sublabel = answer.sublabel || '';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kl-quiz-option';
    btn.setAttribute('data-value', value);
    btn.setAttribute('data-answer-id', answer.id || '');

    // Radio or checkbox indicator
    if (isMulti) {
      btn.innerHTML = '<div class="kl-quiz-check"><span class="kl-quiz-check-icon">' + SVG_CHECK + '</span></div>';
    } else {
      btn.innerHTML = '<div class="kl-quiz-radio"><div class="kl-quiz-radio-dot"></div></div>';
    }

    // Label
    var labelEl = document.createElement(sublabel ? 'div' : 'span');
    labelEl.className = 'kl-quiz-option-label';
    labelEl.textContent = label;
    if (sublabel) {
      var subEl = document.createElement('span');
      subEl.className = 'kl-quiz-option-sublabel';
      subEl.textContent = sublabel;
      labelEl.appendChild(subEl);
    }
    btn.appendChild(labelEl);

    btn.addEventListener('click', function() {
      if (isMulti) {
        self._toggleMultiOption(btn, qId);
      } else {
        self._selectSingleOption(btn, qId);
      }
    });

    return btn;
  };

  QuizInstance.prototype._buildToneButton = function(answer, qId) {
    var self = this;
    var value = answer.text || '';
    var label = answer.text || '';
    var color = answer.color || '#D4A574';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kl-quiz-tone-btn';
    btn.setAttribute('data-value', value);
    btn.setAttribute('data-answer-id', answer.id || '');

    var swatch = document.createElement('div');
    swatch.className = 'kl-quiz-tone-swatch';
    swatch.style.background = color;
    btn.appendChild(swatch);

    btn.appendChild(document.createTextNode(label));

    btn.addEventListener('click', function() {
      self._selectTone(btn, qId);
    });

    return btn;
  };

  /* ═══════════════════════════════════════════════════════════
     SCREEN MANAGEMENT
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._buildScreensList = function() {
    this.screens = [];
    // Intro is managed by Liquid/embed controller, not the screen system
    for (var i = 1; i <= this.questionCount; i++) {
      this.screens.push('q-' + i);
    }
    this.screens.push('email');
    this.screens.push('analyzing');
    this.screens.push('results');
  };

  QuizInstance.prototype._showScreen = function(screenId) {
    // Hide all screens
    var allScreens = this.$$('.kl-quiz-screen');
    for (var i = 0; i < allScreens.length; i++) {
      allScreens[i].classList.remove('active', 'visible');
    }

    // Show target
    var target = this.$('[data-kl-quiz-screen="' + screenId + '"]');
    if (!target) return;

    target.classList.add('active');

    // Double-rAF for transition
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        target.classList.add('visible');
      });
    });

    // Scroll to top of drawer
    var scrollBody = this.root.closest('.kl-quiz-drawer');
    if (scrollBody) {
      var scroll = scrollBody.querySelector('[data-kl-quiz-scroll]');
      if (scroll) scroll.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Update progress
    this._updateProgress();
  };

  QuizInstance.prototype._updateProgress = function() {
    var progressEl = this.$('[data-kl-quiz-progress]');
    var fillEl = this.$('[data-kl-quiz-progress-fill]');
    var textEl = this.$('[data-kl-quiz-progress-text]');
    if (!progressEl || !fillEl || !textEl) return;

    var screenId = this.screens[this.currentIndex];
    var qMatch = screenId ? screenId.match(/^q-(\d+)$/) : null;
    var qNum = qMatch ? parseInt(qMatch[1], 10) : 0;

    if (qNum > 0) {
      var pct = Math.round((qNum / this.questionCount) * 100);
      fillEl.style.width = pct + '%';
      textEl.textContent = qNum + ' of ' + this.questionCount;
      progressEl.style.display = '';
    } else {
      progressEl.style.display = 'none';
    }
  };

  /* ═══════════════════════════════════════════════════════════
     NAVIGATION
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._goNext = function() {
    if (this.currentIndex < this.screens.length - 1) {
      this.currentIndex++;

      // Skip email if already captured
      if (this.screens[this.currentIndex] === 'email' && this.emailCaptured) {
        this.currentIndex++;
      }

      var screenId = this.screens[this.currentIndex];
      if (screenId === 'analyzing') {
        this._runAnalyzing();
      }
      this._showScreen(screenId);
    }
  };

  QuizInstance.prototype._goBack = function() {
    if (this.currentIndex > 0) {
      this.currentIndex--;

      // Skip email going back too
      if (this.screens[this.currentIndex] === 'email' && this.emailCaptured) {
        this.currentIndex--;
      }

      this._showScreen(this.screens[this.currentIndex]);
    }
  };

  QuizInstance.prototype.start = function() {
    var self = this;
    if (this.configLoaded) {
      this.currentIndex = 0;
      this._showScreen(this.screens[0]);
    } else {
      this.loadConfig(function() {
        self.currentIndex = 0;
        self._showScreen(self.screens[0]);
      });
    }
  };

  /* ═══════════════════════════════════════════════════════════
     SELECTION HANDLERS
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._selectSingleOption = function(btn, qId) {
    var self = this;
    var container = btn.closest('[data-kl-quiz-question]');
    var siblings = container.querySelectorAll('.kl-quiz-option');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('selected');
    }
    btn.classList.add('selected');
    this.answers[qId] = btn.getAttribute('data-value');
    this.answerIds[qId] = btn.getAttribute('data-answer-id');

    // Enable continue
    var navBtn = this.$('[data-kl-quiz-nav-next="' + qId + '"]');
    if (navBtn) navBtn.classList.add('enabled');

    // Auto-advance after 350ms
    setTimeout(function() { self._goNext(); }, 350);
  };

  QuizInstance.prototype._toggleMultiOption = function(btn, qId) {
    btn.classList.toggle('selected');

    // Collect all selected values and answer IDs
    var container = btn.closest('[data-kl-quiz-question]');
    var selected = container.querySelectorAll('.kl-quiz-option.selected');
    var values = [];
    var answerIds = [];
    for (var i = 0; i < selected.length; i++) {
      values.push(selected[i].getAttribute('data-value'));
      answerIds.push(selected[i].getAttribute('data-answer-id'));
    }
    this.answers[qId] = values;
    this.answerIds[qId] = answerIds;

    // Enable/disable continue
    var navBtn = this.$('[data-kl-quiz-nav-next="' + qId + '"]');
    if (navBtn) {
      if (values.length > 0) {
        navBtn.classList.add('enabled');
      } else {
        navBtn.classList.remove('enabled');
      }
    }
  };

  QuizInstance.prototype._selectTone = function(btn, qId) {
    var self = this;
    var container = btn.closest('[data-kl-quiz-question]');
    var siblings = container.querySelectorAll('.kl-quiz-tone-btn');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('selected');
    }
    btn.classList.add('selected');
    this.answers[qId] = btn.getAttribute('data-value');
    this.answerIds[qId] = btn.getAttribute('data-answer-id');

    var navBtn = this.$('[data-kl-quiz-nav-next="' + qId + '"]');
    if (navBtn) navBtn.classList.add('enabled');

    // Auto-advance
    setTimeout(function() { self._goNext(); }, 350);
  };

  /* ═══════════════════════════════════════════════════════════
     EMAIL
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._submitEmail = function() {
    var input = this.$('[data-kl-quiz-email-input]');
    var errEl = this.$('[data-kl-quiz-email-error]');
    if (!input) return;

    var email = input.value.trim();
    var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !re.test(email)) {
      input.classList.add('error');
      if (errEl) errEl.classList.add('show');
      input.focus();
      return;
    }

    input.classList.remove('error');
    if (errEl) errEl.classList.remove('show');

    this.email = email;
    this.emailCaptured = true;
    try { sessionStorage.setItem('kl-quiz-email', email); } catch (e) {}

    this._goNext();
  };

  /* ═══════════════════════════════════════════════════════════
     ANALYZING + SUBMIT
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._runAnalyzing = function() {
    var self = this;

    // Reset step states
    var steps = this.$$('.kl-quiz-analyzing-step');
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.remove('show', 'done');
    }
    var errEl = this.$('[data-kl-quiz-analyzing-error]');
    if (errEl) errEl.classList.remove('show');

    // Stagger animation
    var step1 = this.$('[data-kl-quiz-step="1"]');
    var step2 = this.$('[data-kl-quiz-step="2"]');
    var step3 = this.$('[data-kl-quiz-step="3"]');

    setTimeout(function() { if (step1) step1.classList.add('show'); }, 400);
    setTimeout(function() { if (step1) step1.classList.add('done'); }, 1200);
    setTimeout(function() { if (step2) step2.classList.add('show'); }, 1400);
    setTimeout(function() { if (step2) step2.classList.add('done'); }, 2200);
    setTimeout(function() { if (step3) step3.classList.add('show'); }, 2400);
    setTimeout(function() { if (step3) step3.classList.add('done'); }, 3200);

    // Build responses payload from answer IDs
    // Backend expects { question_id, answer_ids: string[] } per question
    var responses = [];
    for (var key in this.answerIds) {
      var ids = this.answerIds[key];
      if (Array.isArray(ids)) {
        responses.push({ question_id: key, answer_ids: ids });
      } else {
        responses.push({ question_id: key, answer_ids: [ids] });
      }
    }

    // Submit to API concurrently with animation
    var animDone = false;
    var submitDone = false;
    var submitResult = null;
    var submitError = false;

    var animTimer = setTimeout(function() {
      animDone = true;
      if (submitDone && !submitError) self._onAnalyzingComplete(submitResult);
    }, 3600);

    this.submitPromise = fetch(API_BASE + '/' + encodeURIComponent(this.slug) + '/respond', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses: responses })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    })
    .then(function(data) {
      submitResult = data;
      submitDone = true;
      self.sessionId = data.session_id || null;
      // Preload product data
      var handles = data.recommended_products || [];
      self.recommendedHandles = handles;

      // Subscribe email (separate call, non-blocking)
      if (self.email && self.sessionId) {
        fetch(API_BASE + '/' + encodeURIComponent(self.slug) + '/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: self.email, session_id: self.sessionId })
        }).catch(function() { /* subscribe failure is non-blocking */ });
      }

      return self._preloadProducts(handles);
    })
    .then(function() {
      if (animDone) self._onAnalyzingComplete(submitResult);
    })
    .catch(function() {
      submitError = true;
      clearTimeout(animTimer);
      if (errEl) errEl.classList.add('show');
    });
  };

  QuizInstance.prototype._preloadProducts = function(handles) {
    // Load product data from Liquid-serialized JSON (theme editor blocks)
    if (!this._liquidProducts) {
      this._liquidProducts = {};
      try {
        var jsonEl = document.getElementById('klQuizProducts');
        if (jsonEl) {
          var arr = JSON.parse(jsonEl.textContent);
          for (var j = 0; j < arr.length; j++) {
            this._liquidProducts[arr[j].handle] = arr[j];
          }
        }
      } catch (e) { /* parse error */ }
    }

    // Match recommended handles to Liquid product data
    for (var i = 0; i < handles.length; i++) {
      if (this._liquidProducts[handles[i]]) {
        this.productCache[handles[i]] = this._liquidProducts[handles[i]];
      }
    }

    return Promise.resolve();
  };

  QuizInstance.prototype._onAnalyzingComplete = function(submitData) {
    this._buildResults(submitData);
    this.currentIndex = this.screens.indexOf('results');
    this._showScreen('results');

    // Stagger result card animations
    var self = this;
    var els = this.$$('.kl-quiz-result-enter');
    for (var i = 0; i < els.length; i++) {
      (function(el, delay) {
        setTimeout(function() { el.classList.add('show'); }, delay);
      })(els[i], 150 * i);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     RESULTS RENDERING
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._buildResults = function(submitData) {
    var heroEl = this.$('[data-kl-quiz-results-hero]');
    var contentEl = this.$('[data-kl-quiz-results-content]');
    if (!heroEl || !contentEl) return;

    // Collect all recommended products from cache
    var handles = this.recommendedHandles;
    var products = [];
    for (var i = 0; i < handles.length; i++) {
      if (this.productCache[handles[i]]) products.push(this.productCache[handles[i]]);
    }

    if (products.length === 0) {
      contentEl.innerHTML = '<div class="kl-quiz-error"><p>We couldn\'t load your results. Please try again.</p><button type="button" class="kl-quiz-error-retry" data-kl-quiz-retry-submit>Try again</button></div>';
      return;
    }

    var product = products[0];
    var kitProduct = products.length > 1 ? products[1] : null;

    // Hero
    heroEl.innerHTML =
      '<div class="kl-quiz-match-badge">' + SVG_MATCH_CHECK + ' Your match</div>' +
      '<h2>' + this._getHeroHeadline() + '</h2>' +
      '<p>' + this._getHeroSubline() + '</p>';

    var html = '';

    // Primary product card
    html += this._buildProductCard(product, 'Top pick');

    // Kit upsell card (if API returned a second product)
    if (kitProduct) {
      html += '<div class="kl-quiz-upgrade-divider kl-quiz-result-enter">Want the full routine?</div>';
      html += this._buildProductCard(kitProduct, 'Complete routine');
    }

    // Retake
    html += '<div class="kl-quiz-result-enter">';
    html += '<button type="button" class="kl-quiz-retake" data-kl-quiz-retake>Retake the quiz</button>';
    html += '</div>';

    contentEl.innerHTML = html;

    // Bind retake
    var retakeBtn = this.$('[data-kl-quiz-retake]');
    if (retakeBtn) {
      var self = this;
      retakeBtn.addEventListener('click', function() { self._retake(); });
    }
  };

  QuizInstance.prototype._buildProductCard = function(product, badge) {
    var html = '<div class="kl-quiz-product-card kl-quiz-result-enter">';

    // Full-width badge bar above image
    if (badge) {
      html += '<div class="kl-quiz-product-badge">' + this._esc(badge) + '</div>';
    }

    // Product image
    html += '<div class="kl-quiz-product-image-wrap">';
    if (product.image) {
      html += '<a href="' + product.url + '" tabindex="-1" aria-hidden="true">';
      html += '<img class="kl-quiz-product-image" src="' + product.image + '" alt="' + this._esc(product.title) + '" loading="lazy">';
      html += '</a>';
    }
    html += '</div>';

    html += '<div class="kl-quiz-product-info">';

    // Eyebrow / concern
    if (product.concern) {
      html += '<div class="kl-quiz-product-concern">' + this._esc(product.concern) + '</div>';
    }

    // Product name (linked)
    html += '<h4 class="kl-quiz-product-name"><a href="' + product.url + '">' + this._esc(product.title) + '</a></h4>';

    if (product.subtitle) {
      html += '<div class="kl-quiz-product-subtitle">' + this._esc(product.subtitle) + '</div>';
    }
    if (product.description) {
      html += '<p class="kl-quiz-product-desc">' + this._esc(product.description) + '</p>';
    }

    // Star rating
    if (product.rating && product.review_count) {
      var r = typeof product.rating === 'number' ? product.rating.toFixed(1) : product.rating;
      var c = product.review_count;
      html += '<div class="kl-quiz-rating">';
      html += '<span class="kl-quiz-stars" aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</span>';
      html += '<span class="kl-quiz-rating-text">' + r + ' (' + c + ' review' + (c !== 1 ? 's' : '') + ')</span>';
      html += '</div>';
    }

    // Price with savings
    html += '<div class="kl-quiz-product-price">';
    html += '<span class="kl-quiz-price-current">' + product.price + '</span>';
    if (product.compare_price) {
      html += '<span class="kl-quiz-price-compare">' + product.compare_price + '</span>';
      if (product.save_pct > 0) {
        html += '<span class="kl-quiz-save-tag">Save ' + product.save_pct + '%</span>';
      }
    }
    html += '</div>';

    // CTA
    html += '<a href="' + product.url + '" class="kl-quiz-cta">' + this._esc(product.cta_text || 'Shop now') + '</a>';
    html += '</div></div>';
    return html;
  };

  /* ── Escape HTML ── */
  QuizInstance.prototype._esc = function(str) {
    if (!str) return '';
    var el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  };

  /* ── Build star rating SVGs from a decimal rating ── */
  QuizInstance.prototype._buildStars = function(rating) {
    var html = '<div class="kl-quiz-stars">';
    for (var i = 1; i <= 5; i++) {
      if (rating >= i) {
        html += SVG_STAR;
      } else if (rating >= i - 0.5) {
        // Half star: clip the fill
        html += '<svg width="16" height="16" viewBox="0 0 24 24" stroke="#f7aea6" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><defs><clipPath id="klHalf' + i + '"><rect x="0" y="0" width="12" height="24"/></clipPath></defs><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" clip-path="url(#klHalf' + i + ')" fill="#f7aea6"/><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>';
      } else {
        // Empty star
        html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f7aea6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>';
      }
    }
    html += '</div>';
    return html;
  };

  /* ── Format price using Shopify's currency format ── */
  QuizInstance.prototype._formatMoney = function(priceStr) {
    var cents = Math.round(parseFloat(priceStr) * 100);
    var fmt = (window.themeVariables && window.themeVariables.settings &&
      (window.themeVariables.settings.currencyCodeEnabled
        ? window.themeVariables.settings.moneyWithCurrencyFormat
        : window.themeVariables.settings.moneyFormat)) || '${{amount}}';
    var dollars = (cents / 100).toFixed(2);
    var dollarsNoDecimals = Math.floor(cents / 100).toString();
    return fmt
      .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', dollarsNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', dollarsNoDecimals)
      .replace('{{amount}}', dollars);
  };

  /* ═══════════════════════════════════════════════════════════
     PERSONALIZED COPY (client-side mapping — tech debt)
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._findAnswer = function(slug) {
    // Try to find answer by question slug or id containing the keyword
    for (var key in this.answers) {
      if (key === slug || key.indexOf(slug) !== -1) return this.answers[key];
    }
    // No positional fallback — wrong position = wrong copy. Return null, let caller use defaults.
    return null;
  };

  QuizInstance.prototype._getHeroHeadline = function() {
    var statement = this._findAnswer('statement');
    if (statement === 'no-makeup') return 'Your makeup-free mornings start here.';
    if (statement === 'love-mirror') return "Here's how to love what you see.";
    if (statement === 'comfortable') return 'Comfortable in your skin. For real this time.';
    if (statement === 'age-gracefully') return 'Age on your terms.';
    return 'We found your match.';
  };

  QuizInstance.prototype._getHeroSubline = function() {
    return "Based on your answers, here's what we recommend for your skin.";
  };

  QuizInstance.prototype._getReasons = function(productHandle) {
    var concern = this._findAnswer('concern');
    var worry = this._findAnswer('worry');
    var reasons = [];

    var concernReasons = {
      'hyperpigmentation': 'Formulated specifically for dark spots, uneven tone, and stubborn hyperpigmentation.',
      'acne': 'Targets breakouts and excess oil from the inside with anti-inflammatory botanicals.',
      'aging': "Supports collagen production and skin elasticity with ingredients you can't get from a serum.",
      'undereye': 'Covers a broad spectrum of skin concerns including under-eye support through overall skin nourishment.',
      'general': 'A comprehensive approach that supports skin health across every major concern.',
      'redness': "Works from within to support your skin's natural balance and resilience."
    };
    reasons.push(concernReasons[concern] || concernReasons['general']);

    if (worry === 'wont-work') {
      reasons.push("Backed by 2,000+ reviews from women who felt the same way before they started. Plus a 45-day money-back guarantee if it's not for you.");
    } else if (worry === 'trendy') {
      reasons.push('Not a trend. These are research-backed ingredients that support real biological processes in your skin.');
    } else if (worry === 'side-effects') {
      reasons.push('Made with gentle, well-studied ingredients. No harsh chemicals, no prescription-strength compounds.');
    } else if (worry === 'routine') {
      reasons.push("Just two capsules a day. Morning and evening. That's it. Nothing to layer, mix, or remember.");
    }

    if (['clear-complexion-kit', 'hyper-renew-kit', 'perfect-skin-kit'].indexOf(productHandle) !== -1) {
      reasons.push('You save money buying the kit versus purchasing each product separately.');
    }

    return reasons;
  };

  QuizInstance.prototype._getGuaranteeCopy = function() {
    var worry = this._findAnswer('worry');
    if (worry === 'wont-work') {
      return "We get it. You've been let down before. That's why every order is backed by our <strong>45-day money-back guarantee</strong>. If you don't see a difference, you get your money back. No questions.";
    } else if (worry === 'trendy') {
      return "This isn't a fad. But if it doesn't earn your trust, our <strong>45-day money-back guarantee</strong> means you're never stuck.";
    } else if (worry === 'side-effects') {
      return "Made with gentle, well-studied ingredients. And if anything doesn't agree with you, our <strong>45-day money-back guarantee</strong> has you covered.";
    }
    return "Every order is protected by our <strong>45-day money-back guarantee</strong>. If it's not right for you, you get your money back.";
  };

  QuizInstance.prototype._getReviewQuote = function() {
    var concern = this._findAnswer('concern');
    var quotes = {
      'hyperpigmentation': { text: "I've tried everything for my dark spots. This is the first thing that actually made a difference I could see in the mirror.", name: 'Verified Customer' },
      'acne': { text: "My skin hasn't been this calm in years. The breakouts slowed down and my confidence went way up.", name: 'Verified Customer' },
      'aging': { text: 'My skin looks plumper and brighter. People keep asking what I changed in my routine.', name: 'Verified Customer' },
      'undereye': { text: "I noticed my overall complexion looking healthier within the first month. Wish I'd started sooner.", name: 'Verified Customer' },
      'general': { text: "I noticed my overall complexion looking healthier within the first month. Wish I'd started sooner.", name: 'Verified Customer' },
      'redness': { text: 'My skin feels so much calmer now. Less reactive, less red. I actually feel comfortable going without foundation.', name: 'Verified Customer' }
    };
    return quotes[concern] || quotes['general'];
  };

  QuizInstance.prototype._getProofImages = function(handle) {
    var proofMap = {
      'hyperpigmentation-cleanse-capsules': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Martina_LoCricchio_before_and_after_300x_2x.progressive_744e0231-cf46-4e30-977b-69ca23ebcfd8.jpg?v=1614776404',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Andrea_Munoz_300x_2x.progressive_e7998406-afa5-44c3-9954-c372f5934758.jpg?v=1614776404'
      ],
      'acne-cleanse-capsules': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Taylor_Result.jpg?v=1614776849',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Jaira_Result_84fb6a5d-8e70-4328-b0cc-5f55ecf16239.jpg?v=1614776849'
      ],
      'anti-aging-complex': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Martina_LoCricchio_before_and_after_300x_2x.progressive_744e0231-cf46-4e30-977b-69ca23ebcfd8.jpg?v=1614776404',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Andrea_Munoz_300x_2x.progressive_e7998406-afa5-44c3-9954-c372f5934758.jpg?v=1614776404'
      ],
      'clear-complexion-kit': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Taylor_Result.jpg?v=1614776849',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Andrea_Munoz_300x_2x.progressive_e7998406-afa5-44c3-9954-c372f5934758.jpg?v=1614776404'
      ],
      'hyper-renew-kit': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Martina_LoCricchio_before_and_after_300x_2x.progressive_744e0231-cf46-4e30-977b-69ca23ebcfd8.jpg?v=1614776404',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Andrea_Munoz_300x_2x.progressive_e7998406-afa5-44c3-9954-c372f5934758.jpg?v=1614776404'
      ],
      'perfect-skin-kit': [
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Andrea_Munoz_300x_2x.progressive_e7998406-afa5-44c3-9954-c372f5934758.jpg?v=1614776404',
        'https://cdn.shopify.com/s/files/1/2274/4443/files/Taylor_Result.jpg?v=1614776849'
      ]
    };
    return proofMap[handle] || [];
  };

  /* ═══════════════════════════════════════════════════════════
     RETAKE
     ═══════════════════════════════════════════════════════════ */

  QuizInstance.prototype._retake = function() {
    this.answers = {};
    this.answerIds = {};
    this.sessionId = null;
    this.recommendedHandles = [];

    // Clear selections
    var options = this.$$('.kl-quiz-option');
    for (var i = 0; i < options.length; i++) options[i].classList.remove('selected');
    var tones = this.$$('.kl-quiz-tone-btn');
    for (var j = 0; j < tones.length; j++) tones[j].classList.remove('selected');

    // Disable all continue buttons
    var navBtns = this.$$('.kl-quiz-next');
    for (var k = 0; k < navBtns.length; k++) navBtns[k].classList.remove('enabled');

    // Reset analyzing steps
    var steps = this.$$('.kl-quiz-analyzing-step');
    for (var s = 0; s < steps.length; s++) steps[s].classList.remove('show', 'done');

    // Clear email input (but keep emailCaptured true)
    var emailInput = this.$('[data-kl-quiz-email-input]');
    if (emailInput) emailInput.value = '';

    // Go back to start
    this.currentIndex = 0;
    this._showScreen(this.screens[0]);
  };


  /* ═══════════════════════════════════════════════════════════
     DRAWER CONTROLLER
     ═══════════════════════════════════════════════════════════ */

  var drawerInstance = null;
  var drawerInitialized = false;
  var savedScrollY = 0;

  function getDrawerElements() {
    var wrap = document.getElementById('klQuizDrawerWrap');
    if (!wrap) return null;
    return {
      wrap: wrap,
      backdrop: wrap.querySelector('[data-kl-quiz-backdrop]'),
      drawer: wrap.querySelector('[data-kl-quiz-drawer]'),
      closeBtn: wrap.querySelector('[data-kl-quiz-close]'),
      core: wrap.querySelector('.kl-quiz-core')
    };
  }

  function initDrawer() {
    if (drawerInitialized) return;
    drawerInitialized = true;

    var els = getDrawerElements();
    if (!els || !els.core) return;

    // Remove anti-FOUC
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        els.wrap.classList.remove('kl-quiz-no-fouc');
      });
    });

    // Create quiz instance for drawer
    drawerInstance = new QuizInstance(els.core);

    // Close triggers
    if (els.backdrop) {
      els.backdrop.addEventListener('click', closeDrawer);
    }
    if (els.closeBtn) {
      els.closeBtn.addEventListener('click', closeDrawer);
    }
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && els.drawer && els.drawer.classList.contains('open')) closeDrawer();
    });

    // Focus trap
    if (els.drawer) {
      els.drawer.addEventListener('keydown', function(e) {
        if (e.key !== 'Tab') return;
        var focusable = els.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
  }

  function openDrawer() {
    var els = getDrawerElements();
    if (!els) return;

    // Close other drawers
    document.documentElement.dispatchEvent(new CustomEvent('klCloseAllDrawers'));

    // Close Stretch mobile menu if open
    try {
      var menuDrawer = document.getElementById('menu-drawer');
      if (menuDrawer && menuDrawer.open) {
        menuDrawer.hide();
      }
    } catch (e) {}

    // iOS scroll lock
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('kl-quiz-open');
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.width = '100%';

    // Open
    if (els.backdrop) els.backdrop.classList.add('open');
    if (els.drawer) {
      els.drawer.classList.add('open');
      els.drawer.setAttribute('aria-hidden', 'false');
    }

    // Init quiz if first time
    if (!drawerInstance) {
      initDrawer();
    }
    if (drawerInstance && !drawerInstance.configLoaded && !drawerInstance.configError) {
      drawerInstance.start();
    } else if (drawerInstance && drawerInstance.configLoaded && drawerInstance.screens.length > 0 && drawerInstance.currentIndex === 0) {
      drawerInstance._showScreen(drawerInstance.screens[0]);
    }

    // Clean hash
    if (window.location.hash === '#skin-quiz') {
      try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) {}
    }
  }

  function closeDrawer() {
    var els = getDrawerElements();
    if (!els) return;

    if (els.backdrop) els.backdrop.classList.remove('open');
    if (els.drawer) {
      /* kl-closing carries the close-direction transition. Default state has
         transition: none so the drawer can never animate on page load. */
      els.drawer.classList.add('kl-closing');
      els.drawer.classList.remove('open');
      els.drawer.setAttribute('aria-hidden', 'true');
      var d = els.drawer;
      d.addEventListener('transitionend', function onEnd(e) {
        if (e.target !== d || e.propertyName !== 'transform') return;
        d.classList.remove('kl-closing');
        d.removeEventListener('transitionend', onEnd);
      });
    }

    // iOS scroll restore
    document.body.classList.remove('kl-quiz-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  /* ═══════════════════════════════════════════════════════════
     GLOBAL API + HASH TRIGGER
     ═══════════════════════════════════════════════════════════ */

  window.openKallistiaQuiz = function() {
    initDrawer();
    openDrawer();
  };

  // Hash trigger on links
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (href && href.indexOf('#skin-quiz') !== -1) {
      e.preventDefault();
      window.openKallistiaQuiz();
    }
  });

  // Hash trigger on page load
  if (window.location.hash === '#skin-quiz') {
    // Wait for DOM + CSS
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(window.openKallistiaQuiz, 100);
      });
    } else {
      setTimeout(window.openKallistiaQuiz, 100);
    }
  }

  // Preload config on hover/touch of any quiz trigger (before click)
  function preloadOnIntent() {
    initDrawer();
    if (drawerInstance && !drawerInstance.configLoaded && !drawerInstance.configError) {
      drawerInstance.loadConfig();
    }
  }

  var preloadBound = false;
  function bindPreload() {
    if (preloadBound) return;
    preloadBound = true;

    // Desktop: preload on hover over quiz trigger
    document.addEventListener('mouseover', function onHover(e) {
      var link = e.target.closest('a[href*="#skin-quiz"], [onclick*="openKallistiaQuiz"]');
      if (link) {
        document.removeEventListener('mouseover', onHover);
        preloadOnIntent();
      }
    });

    // Mobile: preload when quiz banner scrolls into view
    var quizBanner = document.getElementById('klQuizEmbedWrap');
    if (quizBanner) {
      var obs = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          preloadOnIntent();
        }
      }, { rootMargin: '200px' });
      obs.observe(quizBanner);
    }
  }

  // Init everything on DOM ready
  function onReady() {
    initDrawer();
    bindPreload();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

})();
